export type AuthMode = 'signin' | 'signup'

export const AUTH_LAST_EMAIL_KEY = 'zypheron.auth.lastEmail'
export const AUTH_LAST_MODE_KEY = 'zypheron.auth.lastMode'
// A plain sign-in (no explicit ?next=) should land back on the marketing
// site, not force a detour into the Cloud/Dashboard app — that's an
// explicit choice (webAppRoutes.loginForEngagements), not the default.
export const DEFAULT_AUTH_NEXT = '/'

export function sanitizeAuthNext(rawNext: string | null | undefined): string {
  if (!rawNext) return DEFAULT_AUTH_NEXT
  if (!rawNext.startsWith('/') || rawNext.startsWith('//')) return DEFAULT_AUTH_NEXT

  try {
    const origin = typeof window !== 'undefined' ? window.location.origin : 'https://app.zypheron.net'
    const parsed = new URL(rawNext, origin)
    if (parsed.origin !== origin) return DEFAULT_AUTH_NEXT
    return `${parsed.pathname}${parsed.search}${parsed.hash}`
  } catch {
    return DEFAULT_AUTH_NEXT
  }
}

export function getRememberedAuthEmail(): string {
  try {
    return window.localStorage.getItem(AUTH_LAST_EMAIL_KEY) || ''
  } catch {
    return ''
  }
}

export function getRememberedAuthMode(): AuthMode | null {
  try {
    const mode = window.localStorage.getItem(AUTH_LAST_MODE_KEY)
    return mode === 'signin' || mode === 'signup' ? mode : null
  } catch {
    return null
  }
}

export function rememberAuthIntent(email: string, mode: AuthMode): void {
  try {
    const normalizedEmail = email.trim().toLowerCase()
    if (normalizedEmail) {
      window.localStorage.setItem(AUTH_LAST_EMAIL_KEY, normalizedEmail)
    }
    window.localStorage.setItem(AUTH_LAST_MODE_KEY, mode)
  } catch {
    // localStorage can be unavailable in private contexts; auth must continue.
  }
}

export function authModeFromPathAndParams(pathname: string, search: string): AuthMode {
  const params = new URLSearchParams(search)
  if (pathname.startsWith('/signup') || params.get('mode') === 'signup' || pathname.startsWith('/join')) {
    return 'signup'
  }
  return getRememberedAuthMode() === 'signin' ? 'signin' : 'signup'
}

export function buildAuthSearch(mode: AuthMode, next: string): string {
  const params = new URLSearchParams()
  if (mode === 'signup') params.set('mode', 'signup')
  params.set('next', sanitizeAuthNext(next))
  return params.toString()
}
